import {
  Component,
  ChangeDetectionStrategy,
  Input,
  OnChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { fadeIn } from '@animations/fade-in';
import { SabreText } from '@shared/components/sabre-text-dialog/models/sabre-text';
import { SabreTextDialogComponent } from '@shared/components/sabre-text-dialog/sabre-text-dialog.component';
import { RequestState } from '@shared/models/request-state';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { CrewStore } from '../crew.store';
import { CrewListView } from '../models/view/crew-list.view';
import { CrewRequest } from '../models/request/crew.request';
import { CrewLocationView } from '../models/view/crew-location.view';
import { LegView } from '@sequence/models/view/leg.view';
import { CrewHistoryService } from '../crew-history.service';

@Component({
  selector: 'app-crew-card',
  templateUrl: './crew-card.component.html',
  styleUrls: ['./crew-card.component.scss'],
  standalone: false,
  animations: [fadeIn],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CrewCardComponent implements OnChanges {
  @Input() leg?: LegView;
  crew$?: Observable<RequestState<CrewListView>>;
  crewLocation$?: Observable<RequestState<CrewLocationView>>;
  showSeqNumbers$ = this.crewStore.showSeqNumbers$;
  crewHistory$ = this.crewHistoryService.getFlightsFlownWith('782637');

  // Used to keep Sabre Text synced when crew list updates.
  private _sabreText$ = new BehaviorSubject<RequestState<SabreText> | null>(
    null,
  );

  constructor(
    private crewStore: CrewStore,
    private crewHistoryService: CrewHistoryService,
    private dialog: MatDialog,
  ) {}

  ngOnChanges(): void {
    // Update crew observable when @Input leg changes.
    this.setCrewObservable();
  }

  onSeqNumToggleChanged(event: MatSlideToggleChange): void {
    this.crewStore.setShowSeqNumbers(event.checked);
  }

  onViewNsClick(): void {
    if (this._sabreText$) {
      this.dialog.open(SabreTextDialogComponent, {
        data: {
          sabreText$: this._sabreText$.asObservable(),
        },
        panelClass: 'sabre-text',
        maxHeight: '95vh',
        maxWidth: '100vw',
        autoFocus: false,
      });
    }
  }

  // Load/refresh crew data.
  onExpanded(): void {
    this.setCrewObservable();
  }

  refreshCrew(): void {
    this.setCrewObservable(true);
  }

  // Set/reset crew observable.
  private setCrewObservable(isRefresh = false): void {
    if (this.leg) {
      const crewRequest: CrewRequest = {
        flightNumber: this.leg.flightNumber,
        depStation: this.leg.depStation,
        date: this.leg.depTimeSched?.toISODate() || '',
      };

      this.crew$ = this.crewStore.getCrew(crewRequest, isRefresh).pipe(
        tap((requestState) => {
          this.updateSabreText(requestState);
        }),
      );

      this.crewLocation$ = this.crewStore.getCrewLocation(
        crewRequest,
        isRefresh,
      );
    }
  }

  /** Update the sabreText$ observable when the crew list changes. */
  private updateSabreText(requestState: RequestState<CrewListView>): void {
    if (requestState.response?.gate?.raw) {
      const sabreText = new SabreText(
        requestState.response.gate.raw,
        requestState.response.timestamp.toISO(),
      );
      this._sabreText$.next(requestState.into<SabreText>(sabreText));
    } else {
      this._sabreText$.next(requestState.into<SabreText>());
    }
  }
}
